// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// game.ts — a mode-aware wrapper around rules.ts: it sets up the opening, records the ply log, and
// can describe the finished race. Still no DOM and no network.
//
// THE PLY LOG IS THE WHOLE MULTIPLAYER STATE. Bracken is lockstep, and there is nothing random and
// nothing hidden, so a peer that has the mode, the runner order and the list of plies has the game
// — bit for bit. That is why `applyPly`/`plies` exist rather than a snapshot format: the log IS the
// snapshot, it is a few bytes per move, and replaying it through the same generator is what proves
// the two peers agree instead of hoping they do.

import { modeOf, type Mode, type ModeId } from './modes';
import {
  EAST,
  NORTH,
  SOUTH,
  WEST,
  atGoal,
  cellAt,
  createState,
  distance,
  hedgeLegal,
  hedgesLeft,
  pawnMoves,
  play,
  retire,
  type Cell,
  type Goal,
  type Hedge,
  type Move,
  type State,
} from './rules';

/** Where each runner starts and which edge it races to, for a race of 2, 3 or 4. */
export function seatsFor(size: number, count: number): Array<{ at: Cell; goal: Goal }> {
  const m = (size - 1) / 2;
  const all = [
    { at: cellAt(size, size - 1, m), goal: NORTH as Goal },
    { at: cellAt(size, 0, m), goal: SOUTH as Goal },
    { at: cellAt(size, m, size - 1), goal: WEST as Goal },
    { at: cellAt(size, m, 0), goal: EAST as Goal },
  ];
  return all.slice(0, Math.max(2, Math.min(4, count)));
}

/**
 * One ply, as a single integer, because it is the only thing that ever crosses the wire.
 *   step  -> cell (0 .. size*size-1)
 *   hedge -> 1000 + hedge
 * A number this small keeps a whole game's log under a hundred bytes, which is what lets every
 * message carry the ENTIRE log and makes a dropped message a non-event.
 */
export const PLY_HEDGE_BASE = 1000;
/** A seat leaving the race. Logged like any other ply — see rules.retire. */
export const PLY_RETIRE_BASE = 5000;
export const stepPly = (cell: Cell): number => cell;
export const hedgePly = (h: Hedge): number => PLY_HEDGE_BASE + h;
export const retirePly = (seat: number): number => PLY_RETIRE_BASE + seat;
export const isHedgePly = (p: number): boolean => p >= PLY_HEDGE_BASE && p < PLY_RETIRE_BASE;
export const isRetirePly = (p: number): boolean => p >= PLY_RETIRE_BASE;

export function moveOfPly(p: number): Move {
  return isHedgePly(p) ? { kind: 'hedge', hedge: p - PLY_HEDGE_BASE } : { kind: 'step', to: p };
}

export function plyOfMove(m: Move): number {
  return m.kind === 'hedge' ? hedgePly(m.hedge) : stepPly(m.to);
}

export interface Standing {
  /** Index into the runner list. */
  seat: number;
  /** Steps from the goal edge right now — 0 means home. */
  toGo: number;
  /** Hedges this runner may still plant. */
  hedges: number;
  /** How many hedges this runner actually planted. */
  planted: number;
  /** How many steps it took, for the summary. */
  steps: number;
}

export class Game {
  readonly mode: Mode;
  readonly state: State;
  /** Every ply played, in order. This is the entire shared state. */
  readonly plies: number[] = [];
  /** Hedges planted, by seat — kept alongside because the state only records the total. */
  private plantedBy: number[];
  private stepsBy: number[];
  /** Set when the race ended without a winner (the ply cap), so the UI can say so honestly. */
  stalled = false;

  constructor(modeId: ModeId | Mode, runners: number) {
    this.mode = typeof modeId === 'string' ? modeOf(modeId) : modeId;
    const seats = seatsFor(this.mode.size, runners);
    this.state = createState(
      this.mode.size,
      seats.map((s) => ({ at: s.at, goal: s.goal, hedges: this.mode.hedges })),
      this.mode.pool > 0 ? this.mode.pool : -1,
    );
    this.plantedBy = seats.map(() => 0);
    this.stepsBy = seats.map(() => 0);
  }

  get size(): number {
    return this.mode.size;
  }

  get turn(): number {
    return this.state.turn;
  }

  get winner(): number {
    return this.state.winner;
  }

  get over(): boolean {
    return this.state.winner >= 0 || this.stalled;
  }

  /** Legal pawn steps for whoever is to move. */
  steps(): Cell[] {
    return this.over ? [] : pawnMoves(this.state, this.state.turn);
  }

  /** Every hedge the runner to move could legally plant. The renderer aims at THIS list, so an
   *  illegal hedge is not something the player can even point at. */
  legalHedges(): Hedge[] {
    if (this.over || hedgesLeft(this.state, this.state.turn) <= 0) return [];
    const span = this.size - 1;
    const out: Hedge[] = [];
    for (let post = 0; post < span * span; post++) {
      for (const o of [0, 1] as const) {
        const h = post * 2 + o;
        if (hedgeLegal(this.state, h)) out.push(h);
      }
    }
    return out;
  }

  hedgesLeft(seat: number): number {
    return hedgesLeft(this.state, seat);
  }

  /** Apply a ply. Returns false — changing nothing — if it was not legal, which is also how a
   *  peer's log is validated on arrival. */
  applyPly(p: number): boolean {
    if (!Number.isInteger(p) || p < 0) return false;
    const who = this.state.turn;

    if (isRetirePly(p)) {
      if (!retire(this.state, p - PLY_RETIRE_BASE)) return false;
      this.plies.push(p);
      return true;
    }

    const m = moveOfPly(p);
    if (!play(this.state, m)) return false;
    this.plies.push(p);
    if (m.kind === 'hedge') this.plantedBy[who]++;
    else this.stepsBy[who]++;
    if (this.state.winner < 0 && this.state.ply >= this.mode.plyCap) this.stalled = true;
    return true;
  }

  /** Retire a seat, as a logged ply. Returns the ply so a caller can broadcast it. */
  retireSeat(seat: number): number | null {
    return this.applyPly(retirePly(seat)) ? retirePly(seat) : null;
  }

  retired(seat: number): boolean {
    return this.state.runners[seat]?.retired === true;
  }

  play(m: Move): boolean {
    return this.applyPly(plyOfMove(m));
  }

  /** Steps from home for a seat, as the summary and the HUD both want it. */
  toGo(seat: number): number {
    const r = this.state.runners[seat];
    if (atGoal(this.size, r.goal, r.at)) return 0;
    const d = distance(this.size, this.state.walls, r.at, r.goal);
    return d < 0 ? Infinity : d;
  }

  /**
   * Everyone's result, not just the local player's (principle #9). For a race the honest summary is
   * how far from home each runner actually got, what they spent getting there, and — because this
   * game has a knowable best line — how far the loser still had to go.
   */
  standings(): Standing[] {
    return this.state.runners.map((_, seat) => ({
      seat,
      toGo: this.toGo(seat),
      hedges: this.hedgesLeft(seat),
      planted: this.plantedBy[seat],
      steps: this.stepsBy[seat],
    }));
  }

  /** Rebuild a game from a mode, a runner count and a ply log. The whole of lockstep sync. */
  static replay(modeId: ModeId | Mode, runners: number, plies: readonly number[]): Game | null {
    const g = new Game(modeId, runners);
    for (const p of plies) {
      if (!g.applyPly(p)) return null;
    }
    return g;
  }
}
